import { useCallback, useEffect, useRef, useState } from 'react';
import { BACKEND_URL } from '../lib/config';
import { tokens } from '../lib/tokens';

const PENDING_STORAGE_PREFIX = 'sabia:pending:';

// Local write-ahead log for unACKed edits. Survives page refresh and chapter
// switch so we can replay the user's content if the server never received it
// (e.g. WebSocket dropped before the edit reached the hub).
function readStoredPending(chapterId) {
  if (!chapterId) return null;
  try {
    const raw = localStorage.getItem(PENDING_STORAGE_PREFIX + chapterId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.content !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredPending(chapterId, content, ackedVersion) {
  if (!chapterId) return;
  try {
    localStorage.setItem(
      PENDING_STORAGE_PREFIX + chapterId,
      JSON.stringify({ content, ackedVersion, savedAt: Date.now() }),
    );
  } catch {
    // quota / private mode — best effort only
  }
}

function clearStoredPending(chapterId) {
  if (!chapterId) return;
  try {
    localStorage.removeItem(PENDING_STORAGE_PREFIX + chapterId);
  } catch {
    // ignore
  }
}

// Collaborative editing socket for a chapter. Sends/receives full-content
// snapshots (server-authoritative) plus live caret positions + presence.
// Auto-reconnects if the socket drops (e.g. laptop sleep / overnight).
// `onAck` (optional) fires when the server acks a sent edit — it just means
// the hub accepted the snapshot in memory, not that S3 is durable.
// `onSaved` (optional) fires after the hub's periodic flush actually lands the
// snapshot in S3 + Postgres. Callers use this to drive a real “Saved” badge.
export default function useCollab(chapterId, onRemote, onAck, onSaved) {
  const wsRef = useRef(null);
  const onRemoteRef = useRef(onRemote);
  onRemoteRef.current = onRemote;
  const onAckRef = useRef(onAck);
  onAckRef.current = onAck;
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  // Mirror chapterId in a ref so sendEdit / ACK handler always write to the
  // current chapter's storage slot without needing chapterId in deps.
  const chapterIdRef = useRef(chapterId);
  chapterIdRef.current = chapterId;

  const [presence, setPresence] = useState(1);
  const [live, setLive] = useState(false);
  // True after the first `init` message arrives for the current chapter — the
  // editor uses this to show a "loading content…" placeholder instead of an
  // empty doc while the cold S3 fetch / DB lookup is in flight.
  const [loaded, setLoaded] = useState(false);
  // userId -> { pos, name, color, avatar }
  const [cursors, setCursors] = useState({});

  // Tracks last sent-but-not-yet-ACKed content so we can replay it on reconnect
  // if the server never received it (e.g. network dropout before flush).
  const pendingEditRef = useRef(null);
  const lastAckedVersionRef = useRef(0);

  useEffect(() => {
    if (!chapterId) return undefined;
    // New chapter → loading until its first `init` arrives.
    setLoaded(false);
    // Restore any unACKed edit from localStorage — survives refresh + chapter
    // switch. If the server already has it, init's version will be ahead and
    // the init handler will discard the stale pending; otherwise it'll replay.
    const stored = readStoredPending(chapterId);
    pendingEditRef.current = stored?.content ?? null;
    lastAckedVersionRef.current = stored?.ackedVersion ?? 0;
    let unmounted = false;
    let reconnectTimer = null;

    let offlineTimer = null;

    const connect = () => {
      const base = BACKEND_URL.replace(/^http/, 'ws');
      const url = `${base}/api/v1/chapters/${chapterId}/ws?access_token=${encodeURIComponent(
        tokens.access || ''
      )}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        clearTimeout(offlineTimer);
        setLive(true);
      };
      ws.onclose = () => {
        setCursors({});
        if (!unmounted) {
          // Grace window before marking offline — hides transient reconnect flicker
          offlineTimer = setTimeout(() => setLive(false), 1800);
          reconnectTimer = setTimeout(connect, 2000);
        }
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (e) => {
        let msg;
        try {
          msg = JSON.parse(e.data);
        } catch {
          return;
        }
        switch (msg.type) {
          case 'init': {
            const pending = pendingEditRef.current;
            const initVersion = msg.version || 0;
            // Re-send unACKed edits only if server is at or behind our last
            // confirmed version — guards against clobbering newer multi-user state.
            const shouldResend = pending !== null && initVersion <= lastAckedVersionRef.current;
            if (initVersion > lastAckedVersionRef.current) {
              lastAckedVersionRef.current = initVersion;
            }
            onRemoteRef.current?.(msg.content || '', 'init');
            setLoaded(true);
            if (shouldResend) {
              const wsCurrent = wsRef.current;
              if (wsCurrent?.readyState === WebSocket.OPEN) {
                wsCurrent.send(JSON.stringify({ type: 'edit', content: pending }));
              }
            } else if (pending !== null) {
              // Server is ahead (multi-user edits landed first). Drop stale
              // pending so we don't keep trying to replay it forever.
              pendingEditRef.current = null;
              clearStoredPending(chapterIdRef.current);
            }
            break;
          }
          case 'edit':
            onRemoteRef.current?.(msg.content || '', msg.type);
            break;
          case 'ack':
            pendingEditRef.current = null;
            if (msg.version) lastAckedVersionRef.current = msg.version;
            clearStoredPending(chapterIdRef.current);
            onAckRef.current?.(msg.version);
            break;
          case 'saved':
            onSavedRef.current?.(msg.version);
            break;
          case 'presence':
            setPresence(msg.count || 1);
            break;
          case 'cursor':
            setCursors((c) => ({
              ...c,
              [msg.userId]: {
                pos: msg.pos ?? 0,
                name: msg.name,
                color: msg.color,
                avatar: msg.avatar,
              },
            }));
            break;
          case 'left':
            setPresence(msg.count || 1);
            setCursors((c) => {
              const next = { ...c };
              delete next[msg.userId];
              return next;
            });
            break;
          default:
            break;
        }
      };
    };

    connect();
    return () => {
      unmounted = true;
      clearTimeout(reconnectTimer);
      clearTimeout(offlineTimer);
      wsRef.current?.close();
    };
  }, [chapterId]);

  const sendEdit = useCallback((content) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn('[useCollab] sendEdit: ws not ready', {
        exists: !!ws,
        readyState: ws?.readyState,
      });
      return false;
    }
    try {
      ws.send(JSON.stringify({ type: 'edit', content }));
      pendingEditRef.current = content; // cleared when ACK arrives
      writeStoredPending(chapterIdRef.current, content, lastAckedVersionRef.current);
      return true;
    } catch (err) {
      console.error('[useCollab] sendEdit failed', err);
      return false;
    }
  }, []);

  const sendCursor = useCallback((pos, name, color, avatar) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'cursor', pos, name, color, avatar }));
    }
  }, []);

  return { presence, live, loaded, cursors, sendEdit, sendCursor };
}
